/**
 * Natalie telemetry: the Datadog global-context key for the Natalie event id, plus a pure resolver
 * that derives the event id from the current pathname. No React / no monitoring imports so the
 * resolver stays unit-testable in isolation and reusable from the Natalie Redux telemetry listener.
 *
 * The key is flat snake_case (`natalie_event_id`) to match the attribute `conversation-agent` binds
 * and the Natalie dashboards facet on (`@natalie_event_id`).
 */
import { matchPath } from 'react-router-dom';

import { NatalieRoutes } from 'pages/Natalie/routes';

/** Datadog global-context key set on RUM + Logs while a participant is in a Natalie session. */
export const NATALIE_EVENT_ID_CONTEXT_KEY = 'natalie_event_id';

/**
 * Routes under `/natalie` that must NOT carry the tag, checked before the `/natalie/:id` catch-all
 * (otherwise `/natalie/:id` would read `list` / `wizard` as an event id).
 *
 * `List` is matched exactly — it is a leaf index today; switch it to a prefix match if it ever
 * gains sub-routes, otherwise `/natalie/list/...` would fall through and resolve to `'list'`.
 *
 * `Wizard` is matched as a **prefix** (no `exact`): it is a creator/admin surface and the *entire*
 * `/natalie/wizard` tree must be excluded — create (`/natalie/wizard`), edit (`/natalie/wizard/:id`),
 * and any future sub-step (`/natalie/wizard/:id/step`). An exact match would miss deeper paths and
 * let the Details catch-all return `'wizard'` as the id.
 */
const EXCLUDED_EXACT_ROUTES = [NatalieRoutes.List] as const;
const EXCLUDED_PREFIX_ROUTES = [NatalieRoutes.Wizard] as const;

/**
 * Resolve the Natalie event id from a pathname, or `undefined` when the route should not be tagged.
 *
 * For both the authenticated flow (`/natalie/:id/...`) and the accountless flow
 * (`/natalie/:eventId/:inviteCode/...`) the event id is the first path segment after `/natalie`, so
 * a non-exact `/natalie/:id` match captures it for either — no need to enumerate every sub-route or
 * replicate PreApp's invite-code gate.
 */
export const resolveNatalieEventId = (pathname: string): string | undefined => {
  if (
    EXCLUDED_EXACT_ROUTES.some((path) => matchPath(pathname, { path, exact: true })) ||
    EXCLUDED_PREFIX_ROUTES.some((path) => matchPath(pathname, { path }))
  ) {
    return undefined;
  }

  const match = matchPath<{ id?: string }>(pathname, { path: NatalieRoutes.Details });
  return match?.params.id;
};
